import { useCallback, useEffect, useState } from "react";
import {
  fetchFocusedSessionFee as apiFetchFocusedSessionFee,
} from "../conferenceRegistrationApi";

export const useFocusedSessions = ({
  conferenceApi,
  conferenceId,
  focusedSessions,
  focusedSessionAvailability,
  fetchFocusedSessions,
  additionalFees,
  focusedRadio,
  accompanyingPersonFee,
  setRegistrationJustUpdated,
  setFormData,
}) => {
  // ==========================================
  // STATE
  // ==========================================

  const [
    registerForFocusedSessions,
    setRegisterForFocusedSessions,
  ] = useState("No");

  const [
    selectedFocusedSessions,
    setSelectedFocusedSessions,
  ] = useState([]);

  const [
    focusedSessionFees,
    setFocusedSessionFees,
  ] = useState({});


  // ==========================================
  // FETCH FOCUSED SESSION FEE
  // ==========================================

  const fetchFocusedSessionFee = useCallback(
    async (sessionId) => {
      if (!conferenceId || !sessionId) {
        return null;
      }

      try {
        return await apiFetchFocusedSessionFee({
          conferenceApi,
          conferenceId,
          sessionId,
        });
      } catch (error) {
        console.error(
          `Error fetching focused session fee for ${sessionId}:`,
          error
        );

        return null;
      }
    },
    [conferenceApi, conferenceId]
  );


  // ==========================================
  // YES / NO FOCUSED SESSION CHANGE
  // ==========================================

  const handleFocusedSessionsChange = (value) => {
    console.log(
      "Focused Session Registration:",
      value
    );

    setRegistrationJustUpdated(false);

    setRegisterForFocusedSessions(value);

    // Update formData
    setFormData((prev) => ({
      ...prev,
      registerForFocusedSessions: value,

      // If user selects No, clear selected sessions
      ...(value === "No"
        ? {
            selectedFocusedSessions: [],
          }
        : {}),
    }));

    // If user selects Yes and sessions are not loaded
    if (
      value === "Yes" &&
      focusedSessions.length === 0
    ) {
      fetchFocusedSessions();
    }

    // If user selects No, clear selected sessions
    if (value === "No") {
      setSelectedFocusedSessions([]);
    }
  };


  // ==========================================
  // CHECKBOX CHANGE
  // ==========================================

  const handleFocusedSessionCheckboxChange = (
    sessionId
  ) => {
    setRegistrationJustUpdated(false);

    setSelectedFocusedSessions((prev) => {
      const session = focusedSessions.find(
        (focusedSession) =>
          focusedSession.id === sessionId
      );

      const availability =
        focusedSessionAvailability[sessionId];

      const availableSeats = Number(
        availability?.availableSeats ??
          availability?.available_seats ??
          availability?.remainingSeats ??
          session?.availableSeats ??
          session?.available_seats ??
          session?.remainingSeats ??
          0
      );


      // ========================================
      // REMOVE SESSION
      // ========================================

      if (prev.includes(sessionId)) {
        setFocusedSessionFees((prevFees) => {
          const nextFees = {
            ...prevFees,
          };

          delete nextFees[sessionId];

          return nextFees;
        });

        const updatedSessions = prev.filter(
          (id) => id !== sessionId
        );

        console.log(
          "Focused sessions after removing:",
          updatedSessions
        );

        return updatedSessions;
      }


      // ========================================
      // CHECK AVAILABLE SEATS
      // ========================================

      if (availableSeats <= 0) {
        console.warn(
          `⚠️ Cannot select focused session ${sessionId}: no available seats`
        );

        return prev;
      }


      // ========================================
      // ADD SESSION
      // ========================================

      const updatedSessions = [
        ...prev,
        sessionId,
      ];

      console.log(
        "Focused sessions after adding:",
        updatedSessions
      );

      return updatedSessions;
    });
  };


  // ==========================================
  // RADIO CHANGE
  // ==========================================

  const handleFocusedSessionRadioChange = (
    session
  ) => {
    setRegistrationJustUpdated(false);

    if (!session?.id) {
      return;
    }

    setSelectedFocusedSessions([
      session.id,
    ]);
  };


  // ==========================================
  // SYNC SELECTED SESSIONS → FORM DATA
  // ==========================================

  useEffect(() => {
    console.log(
      "🔄 Syncing selected focused sessions:",
      selectedFocusedSessions
    );

    setFormData((prev) => ({
      ...prev,
      selectedFocusedSessions,
    }));
  }, [
    selectedFocusedSessions,
    setFormData,
  ]);


  // ==========================================
  // SYNC REGISTER FOR FOCUSED SESSION → FORM DATA
  // ==========================================

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      registerForFocusedSessions,
    }));
  }, [
    registerForFocusedSessions,
    setFormData,
  ]);


  // ==========================================
  // FETCH FEES FOR SELECTED SESSIONS
  // ==========================================

  useEffect(() => {
    if (
      !conferenceId ||
      selectedFocusedSessions.length === 0
    ) {
      return;
    }

    const sessionsNeedingFees =
      selectedFocusedSessions.filter(
        (sessionId) =>
          !focusedSessionFees[sessionId]
      );

    if (
      sessionsNeedingFees.length === 0
    ) {
      return;
    }

    sessionsNeedingFees.forEach(
      (sessionId) => {
        fetchFocusedSessionFee(sessionId).then(
          (feeData) => {
            if (feeData) {
              setFocusedSessionFees(
                (prevFees) => ({
                  ...prevFees,
                  [sessionId]: feeData,
                })
              );
            }
          }
        );
      }
    );
  }, [
    conferenceId,
    selectedFocusedSessions,
    focusedSessionFees,
    fetchFocusedSessionFee,
  ]);


  // ==========================================
  // FETCH FEES FROM ADDITIONAL FEES
  // ==========================================

  useEffect(() => {
    if (!conferenceId) {
      return;
    }

    const sessionIdsNeedingFees = [
      ...new Set(
        (additionalFees || [])
          .filter(
            (fee) =>
              !accompanyingPersonFee ||
              fee.feeTypeId !==
                accompanyingPersonFee.id
          )
          .map(
            (fee) => fee.feeTypeId
          )
          .filter(
            (sessionId) =>
              sessionId &&
              !focusedSessionFees[sessionId]
          )
      ),
    ];

    if (
      sessionIdsNeedingFees.length === 0
    ) {
      console.log(
        "✅ All additional fees have rates loaded"
      );

      return;
    }

    console.log(
      "📥 Fetching rates for sessions:",
      sessionIdsNeedingFees
    );

    sessionIdsNeedingFees.forEach(
      (sessionId) => {
        fetchFocusedSessionFee(
          sessionId
        ).then((feeData) => {
          console.log(
            `💰 Fetched rate for session ${sessionId}:`,
            feeData
          );

          if (feeData) {
            setFocusedSessionFees(
              (prevFees) => ({
                ...prevFees,
                [sessionId]: feeData,
              })
            );
          } else {
            console.warn(
              `⚠️ No fee data returned for session ${sessionId}`
            );
          }
        });
      }
    );
  }, [
    conferenceId,
    additionalFees,
    accompanyingPersonFee,
    focusedSessionFees,
    fetchFocusedSessionFee,
  ]);


  // ==========================================
  // FETCH FOCUSED SESSIONS WHEN YES
  // ==========================================

  useEffect(() => {
    if (
      registerForFocusedSessions === "Yes" &&
      conferenceId &&
      focusedSessions.length === 0
    ) {
      fetchFocusedSessions();
    }
  }, [
    registerForFocusedSessions,
    conferenceId,
    focusedSessions.length,
    fetchFocusedSessions,
  ]);


  // ==========================================
  // RETURN
  // ==========================================

  return {
    registerForFocusedSessions,
    setRegisterForFocusedSessions,

    selectedFocusedSessions,
    setSelectedFocusedSessions,

    focusedSessionFees,
    setFocusedSessionFees,

    fetchFocusedSessionFee,

    handleFocusedSessionsChange,
    handleFocusedSessionCheckboxChange,
    handleFocusedSessionRadioChange,
  };
};